import { useEffect, useRef, useState } from 'react';
import { Menu, X, ChevronDown, ArrowUpRight, LogOut } from 'lucide-react';
import { NAV_LINKS } from '../mock';
import LoginModal from './LoginModal';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    setOpen(false);
    toast.success('Signed out');
  };

  const initials = user
    ? user.name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase()
    : '';

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-[#F5F1E8]/85 backdrop-blur-md border-b border-[#0B3B2E]/10' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-[72px] relative">
          <a href="/" className="flex items-center gap-2 group">
            <LogoMark />
            <span className="font-serif text-[22px] font-semibold text-[#0B3B2E] tracking-tight">
              Vector<span className="text-[#C8944B]">FX</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={l.href}
                className="px-4 py-2 text-[14px] font-medium text-[#0E1F1A]/75 hover:text-[#0B3B2E] transition-colors relative group"
              >
                {l.label}
                <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-[#0B3B2E] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
              </a>
            ))}
            <button className="px-4 py-2 text-[14px] font-medium text-[#0E1F1A]/75 hover:text-[#0B3B2E] transition-colors inline-flex items-center gap-1">
              Company <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {/* Sticky Portals link */}
            <button
              onClick={() => setOpen(true)}
              className="px-4 py-2 text-[14px] font-medium text-[#0B3B2E] hover:text-[#12503F] transition-colors inline-flex items-center gap-1 relative group"
            >
              Portals <ArrowUpRight className="w-3.5 h-3.5" />
              <span className="absolute left-4 right-8 -bottom-0.5 h-px bg-[#0B3B2E] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
            </button>
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center gap-2 pl-1 pr-3 h-10 rounded-full border border-[#0B3B2E]/20 hover:border-[#0B3B2E]/45 transition-colors"
                >
                  <span className="w-8 h-8 rounded-full bg-[#0B3B2E] text-[#C8944B] font-medium text-[13px] flex items-center justify-center">
                    {initials}
                  </span>
                  <span className="hidden sm:inline text-[13px] font-medium text-[#0B3B2E]">
                    {user.name.split(' ')[0]}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-[#0B3B2E]/70" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-64 rounded-2xl bg-[#F5F1E8] border border-[#0B3B2E]/15 shadow-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-[#0B3B2E]/10">
                      <p className="text-[13px] font-medium text-[#0B3B2E] truncate">{user.name}</p>
                      <p className="text-[12px] text-[#0E1F1A]/60 truncate">{user.email}</p>
                    </div>
                    <a href="#" className="block px-4 py-2.5 text-[13px] text-[#0E1F1A]/80 hover:bg-[#0B3B2E]/5">Dashboard</a>
                    <a href="#" className="block px-4 py-2.5 text-[13px] text-[#0E1F1A]/80 hover:bg-[#0B3B2E]/5">Settings</a>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-[13px] text-[#0B3B2E] hover:bg-[#0B3B2E]/5 border-t border-[#0B3B2E]/10 inline-flex items-center gap-2"
                    >
                      <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button
                  onClick={() => setLoginOpen(true)}
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[14px] font-medium text-[#0B3B2E] border border-[#0B3B2E]/25 hover:bg-[#0B3B2E] hover:text-[#F5F1E8] transition-all duration-300"
                >
                  Log in
                </button>
                <Button
                  onClick={() => setLoginOpen(true)}
                  className="rounded-full bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] px-5 h-10 text-[14px] font-medium shadow-none group"
                >
                  Open account
                  <ArrowUpRight className="w-4 h-4 ml-1 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Button>
              </>
            )}
            <button
              onClick={() => setOpen(!open)}
              className="ml-1 p-2 rounded-md text-[#0B3B2E] hover:bg-[#0B3B2E]/8 transition-colors"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="bg-[#F5F1E8] border-t border-[#0B3B2E]/10 lg:absolute lg:right-6 lg:top-[72px] lg:w-[360px] lg:rounded-2xl lg:border lg:border-[#0B3B2E]/12 lg:shadow-[0_20px_50px_-15px_rgba(11,59,46,0.25)] lg:mt-2">
            <div className="px-6 py-4 flex flex-col gap-1 lg:py-5">

              {/* Signed-in customer section */}
              {user && (
                <div className="mb-4 pb-4 border-b border-[#0B3B2E]/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#0B3B2E] text-[#C8944B] font-medium text-[13px] flex items-center justify-center">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium text-[#0B3B2E] truncate">Signed in as {user.name}</p>
                      <p className="text-[12px] text-[#0E1F1A]/60 truncate">{user.email}</p>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <a
                      href="#"
                      onClick={() => setOpen(false)}
                      className="text-center py-2 rounded-lg bg-[#0B3B2E]/8 text-[13px] font-medium text-[#0B3B2E] hover:bg-[#0B3B2E]/12 transition-colors"
                    >
                      My account
                    </a>
                    <button
                      onClick={handleLogout}
                      className="py-2 rounded-lg border border-[#0B3B2E]/20 text-[13px] font-medium text-[#0B3B2E] hover:bg-[#0B3B2E]/5 transition-colors inline-flex items-center justify-center gap-1.5"
                    >
                      <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                  </div>
                </div>
              )}

              <p className="lg:hidden text-[10px] uppercase tracking-[0.22em] text-[#0E1F1A]/50 mb-1">Sections</p>
              {NAV_LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="py-3 lg:py-2 text-[16px] lg:text-[14px] font-medium text-[#0B3B2E] border-b border-[#0B3B2E]/10 last:border-0 lg:border-0 lg:hover:bg-[#0B3B2E]/6 lg:rounded-md lg:px-3 transition-colors"
                >
                  {l.label}
                </a>
              ))}
              {!user && (
                <button
                  onClick={() => {
                    setOpen(false);
                    setLoginOpen(true);
                  }}
                  className="lg:hidden mt-3 py-3 text-center rounded-full border border-[#0B3B2E]/25 font-medium text-[#0B3B2E]"
                >
                  Log in
                </button>
              )}

              {/* Portal shortcuts */}
              <div className="mt-5 pt-4 border-t border-[#0B3B2E]/10 lg:mt-3 lg:pt-3">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#0E1F1A]/50 mb-2 lg:px-3">Portals</p>
                <a
                  href="/crm/login"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between py-3 lg:py-2 lg:px-3 lg:rounded-md text-[15px] lg:text-[14px] text-[#0B3B2E] font-medium lg:hover:bg-[#0B3B2E]/6 transition-colors"
                >
                  Staff Login
                  <ArrowUpRight className="w-4 h-4 opacity-70" />
                </a>
                <a
                  href="/partner/login"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between py-3 lg:py-2 lg:px-3 lg:rounded-md text-[15px] lg:text-[14px] text-[#0B3B2E] font-medium border-t border-[#0B3B2E]/10 lg:border-0 lg:hover:bg-[#0B3B2E]/6 transition-colors"
                >
                  Referral partner login
                  <ArrowUpRight className="w-4 h-4 opacity-70" />
                </a>
                <a
                  href="/partner/signup"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between py-3 lg:py-2 lg:px-3 lg:rounded-md text-[14px] lg:text-[13px] text-[#0E1F1A]/70 border-t border-[#0B3B2E]/10 lg:border-0 lg:hover:bg-[#0B3B2E]/6 transition-colors"
                >
                  Become a referral partner
                  <ArrowUpRight className="w-4 h-4 opacity-60" />
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      <LoginModal open={loginOpen} onOpenChange={setLoginOpen} />
    </>
  );
}

function LogoMark() {
  return (
    <div className="relative w-9 h-9 rounded-full bg-[#0B3B2E] flex items-center justify-center overflow-hidden group-hover:rotate-45 transition-transform duration-700">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M4 20 L12 4 L20 20" stroke="#C8944B" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="20" r="1.6" fill="#F5F1E8" />
      </svg>
    </div>
  );
}
